/**
 * RouteDetails accessibility / test identifiers.
 *
 * Byte-identical across iOS (`.accessibilityIdentifier`), Android
 * (`RouteDetailsTestTags.kt`), and Web (`data-testid`) per QA spec §6.
 * Section-indexed ids take the 0-based line-block index `<i>`; middle-stop
 * rows additionally take a row index `<j>`.
 */
export const RouteDetailsTestTags = {
  /** Modal dialog root. */
  root: 'route-details-root',
  /** Routes-collection chip row container. `<N>` = route count. */
  routesCollectionRow: (count: number): string => `routes-collection-row-with-${count}-routes`,
  /** Horizontal scroll inside the chip row. */
  routesCollectionScroll: 'routes-collection-scroll-view',
  /** A single route-variant chip. `<route_id>` = the route's id. */
  routeChip: (routeId: string): string => `route-details-route-chip-${routeId}`,
  /** FirstStop row for line block `<i>`. */
  firstStop: (i: number): string => `route-details-first-row-view-section-${i}`,
  /** Direction row for line block `<i>`. */
  direction: (i: number): string => `route-details-direction-row-view-section-${i}`,
  /** Expand row container for line block `<i>`. */
  expandRow: (i: number): string => `route-details-expand-row-view-section-${i}`,
  /** Expand button for line block `<i>`. */
  expandButton: (i: number): string => `route-details-expand-button-section-${i}`,
  /** Middle stop row `<j>` within line block `<i>`. */
  middleStop: (i: number, j: number): string =>
    `route-details-middle-row-view-section-${i}-row-${j}`,
  /** Last stop row for line block `<i>`. */
  lastStop: (i: number): string => `route-details-last-row-view-section-${i}`,
  /** Transfer row for line block `<i>`. */
  transfer: (i: number): string => `route-details-transfer-row-view-section-${i}`,
  /** Street-walk row for route leg `<i>` (2026-07 street-walk contract). */
  streetWalk: (i: number): string => `route-details-street-walk-row-view-section-${i}`,
  /** Selected-origin endpoint marker before a leading street walk (iOS canon @c9d7ecc61). */
  streetEndpointOrigin: (i: number): string =>
    `route-details-street-endpoint-origin-row-view-section-${i}`,
  /** Selected-destination endpoint marker after a trailing street walk (iOS canon @c9d7ecc61). */
  streetEndpointDestination: (i: number): string =>
    `route-details-street-endpoint-destination-row-view-section-${i}`,
  /** Entry (walk-in) row. */
  entry: 'route-details-entry-row-view',
  /** Exit (walk-out) row. */
  exit: 'route-details-exit-row-view',
  /** Validation (tap-in) row. */
  validationEntry: 'route-details-validation-entry-row-view',
  /** Validation (tap-out) row. */
  validationExit: 'route-details-validation-exit-row-view',
  /** PossibleTransfer car-strip for line block `<i>`. */
  possibleTransfer: (i: number): string => `route-details-possible-transfer-view-section-${i}`,
  /** UpsellBanner. */
  upsellBanner: 'route-details-transfer-spots-upsell-banner',
  /** Map row. */
  map: 'map-view',
  /** Map row when a DIRECT-WALK route is drawn on it (byte-identical to
   *  iOS `map-view-walk-route`). */
  mapWalkRoute: 'map-view-walk-route',
  /** Report-issue row. */
  reportIssue: 'route-details-report-issue-view',
} as const
